import "../../css/Login.css";
import { memo, useCallback, useContext, useMemo, useState } from "react";
import { IconButton } from "@mui/material";
import {
  createUserWithEmailAndPassword,
  deleteUser,
  firebaseAuth,
  signInWithEmailAndPassword,
} from "../../firebase";
import { Colors } from "../../utils/colors";
import CustomTextField from "../../components/CustomTextField";
import { isDesktop } from "react-device-detect";
import { MixpanelContext } from "../../context/AnalyticsService";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import {
  censoredWordsDetected,
  detectedCensored,
  friendlyRoleName,
  isValidEmail,
  MARKITAI,
  PhoneErrorType,
} from "@markit/common.utils";
import VerificationCodeButton, {
  VerificationCodeOrigin,
} from "../../components/Verification/VerificationCodeButton";
import { VerificationState, Event } from "@markit/common.types";
import RectangleButton from "../../components/Buttons/RectangleButton";
import { useLogin } from "../../hooks/useLogin";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { LightTheme, useTheme } from "../../hooks/useTheme";
import LoginForgotPassword from "../../components/Login/LoginForgotPassword";
import AlertContainer from "../../components/Containers/AlertContainer";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import CustomPhoneTextField from "../../components/TextFields/CustomPhoneTextField";
import { showPhoneInputError } from "../../utils/phoneUtils";
import RecaptchaVerifierWrapper from "../../components/Wrappers/RecaptchaVerifierWrapper";
import { Icon } from "@iconify/react";
import TextHoverButton from "../../components/Buttons/TextHoverButton";
import LoginWelcomeBody from "../../components/Login/LoginWelcomeBody";
import { getEventData, getUserDataByEmail } from "../../utils/FirebaseUtils";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { Helmet } from "react-helmet";
import { NavigationId } from "../../navigation/AppParamList";
import LoginOnboardingAccountType from "../../components/Login/LoginOnboardingAccountType";
import { ContainerButton } from "../../components/Buttons/ContainerButton";
import { getShareDomain } from "../../utils/shareLink";
import { SocialSignInButtons } from "../../components/Login/SocialSignInButtons";
import { optInToWeeklyCommunityUpdates } from "../../utils/weeklyUpdatesOptIn";
import { WeeklyUpdatesDisclosure } from "../../components/Login/WeeklyUpdatesDisclosure";

export enum LoginStage {
  PHONE = "Phone",
  EMAIL_LOGIN = "Email Login",
  EMAIL_SIGNUP = "Email Signup",
  ACCOUNT_TYPE = "Account Type",
  FORGOT_PASSWORD = "Forgot Password",
}

const LoginWelcome = memo(function LoginWelcomeFn() {
  const { account } = useSelector(getAccountState);
  const { currentRoleTicket } = account;
  const { preEmail, preLoginStage } = useLocation().state || {}; // preEmail = email input from welcome landing page, preLoginStage = different login stage from welcome landing page
  const mixpanel = useContext(MixpanelContext);
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });

  const { loginWithCredential } = useLogin();

  const [loading, setLoading] = useState(false);
  const [verificationState, setVerificationState] = useState(
    VerificationState.UNVERIFIED
  );
  const [optedOut, setOptedOut] = useState(false);
  const [tempPhoneNumber, setTempPhoneNumber] = useState<string>("");

  const [showCodeInputError, setShowCodeInputError] = useState(false);
  const [showPhoneNumberError, setShowPhoneNumberError] =
    useState<PhoneErrorType>({ isError: false, message: "" });

  const [loginStage, setLoginStage] = useState<LoginStage>(
    preLoginStage ?? LoginStage.EMAIL_LOGIN
  );
  // email login/signup
  const [email, setEmail] = useState<string>(preEmail ?? "");
  const [emailError, setEmailError] = useState({
    status: false,
    message: "",
  });
  const [passwordError, setPasswordError] = useState({
    status: false,
    message: "",
  });
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);

  // email signup
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [signUpFullName, setSignUpFullName] = useState<string>("");
  const [signUpFullNameError, setSignUpFullNameError] = useState({
    status: false,
    message: "",
  });
  const [signUpPhoneNumber, setSignUpPhoneNumber] = useState<string>("");
  const [signUpPhoneNumberError, setSignUpPhoneNumberError] =
    useState<PhoneErrorType>({ isError: false, message: "" });

  const [currentEventForRole, setCurrentEventForRole] = useState<Event>();

  const styles = {
    errorMessage: { fontSize: 12, color: Colors.RED3 },
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (currentRoleTicket) {
      const eventDataForRole = await getEventData(currentRoleTicket.eventId);
      setCurrentEventForRole(eventDataForRole);
    }
  }, [currentRoleTicket]);

  const defaultSubscriber = useMemo(
    () => searchParams.has("subscriber"),
    [searchParams]
  );

  const handleClickShowPassword = useCallback(
    () => setShowPassword(!showPassword),
    [showPassword]
  );

  const handleClickShowConfirmPassword = useCallback(
    () => setShowConfirmPassword(!showConfirmPassword),
    [showConfirmPassword]
  );

  const navigateEmailLoginOnPress = useCallback(() => {
    mixpanel.track("Webapp: User Start Login");
    setLoginStage(LoginStage.EMAIL_LOGIN);
  }, [mixpanel]);

  const navigateForgotPassword = useCallback(() => {
    setLoginStage(LoginStage.FORGOT_PASSWORD);
  }, []);

  const backToEmailSignIn = useCallback(async () => {
    setLoginStage(LoginStage.EMAIL_LOGIN);
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setEmailError({ status: false, message: "" });
    setPasswordError({ status: false, message: "" });
  }, []);

  const roleSignUpOnPress = useCallback(() => {
    setLoginStage(LoginStage.EMAIL_SIGNUP);
    setEmailError({ status: false, message: "" });
    setPasswordError({ status: false, message: "" });
  }, []);

  const logInWithEmailAndPassword = useCallback(async () => {
    try {
      if (!email || !password) {
        setAlertText({
          heading: "One or more of the fields are empty.",
          subHeading: "You must fill them all to continue.",
        });
        return;
      }
      await signInWithEmailAndPassword(firebaseAuth, email, password).then(
        async (credential) => {
          if (credential.user !== null) {
            setLoading(true);
            setEmailError({ status: false, message: "" });
            setPasswordError({ status: false, message: "" });
            loginWithCredential(credential, true);
            setLoading(false);
          }
        }
      );
    } catch (error: any) {
      switch (error.code) {
        case "auth/user-not-found": {
          // auth/user-not-found specifically means no password-based Auth
          // record exists for this email - if a Firestore account doc
          // exists anyway, it must belong to a different-provider (Google)
          // account rather than one that's genuinely never been created.
          const existingAccount = await getUserDataByEmail(
            email.toLowerCase()
          );
          if (existingAccount) {
            setAlertText({
              heading: "This account uses Google sign-in.",
              subHeading:
                'Please use the "Continue with Google" button instead.',
            });
          } else {
            setEmailError({
              status: true,
              message:
                "Account does not exist. Sign up to create an account.",
            });
            setPasswordError({ status: true, message: "" });
          }
          break;
        }
        case "auth/invalid-email":
          setEmailError({
            status: true,
            message: "The email address is poorly formatted.",
          });
          break;
        case "auth/wrong-password":
          setPasswordError({
            status: true,
            message: "The password is invalid. Please try again.",
          });
          break;
        default:
          setEmailError({
            status: true,
            message: "The email or password is invalid. Please try again.",
          });
          setPasswordError({ status: true, message: "" });
          break;
      }
    }
  }, [email, password, loginWithCredential]);

  const registerEmailAndPassword = useCallback(() => {
    const emailLower = email.toLowerCase();
    const trimmedFullName = signUpFullName.trim();
    if (!trimmedFullName || !email || !password || !signUpPhoneNumber) {
      setAlertText({
        heading: "One or more of the fields are empty.",
        subHeading: "You must fill them all to continue.",
      });
      return;
    }
    if (!isValidEmail(emailLower)) {
      setEmailError({
        status: true,
        message: "The email address is poorly formatted.",
      });
      return;
    }
    if (detectedCensored(trimmedFullName)) {
      setSignUpFullNameError({
        status: true,
        message: `Content violation: banned words detected (${censoredWordsDetected(
          trimmedFullName
        )})`,
      });
      return;
    }
    // Phone number is required here — this is how fans opt into weekly
    // community text updates, so we force collection rather than leaving it
    // optional.
    if (showPhoneInputError(signUpPhoneNumber, setSignUpPhoneNumberError)) {
      return;
    }
    if (password !== confirmPassword) {
      setAlertText({
        heading: "Your passwords do not match!",
        subHeading: "",
      });
      return;
    }
    setLoading(true);
    createUserWithEmailAndPassword(firebaseAuth, emailLower, password)
      .then(async (userCredential) => {
        if (userCredential.user !== null) {
          // Check if a social (ie. google) email account already exists
          const existingAccount = await getUserDataByEmail(emailLower);
          if (
            existingAccount &&
            existingAccount.uid !== userCredential.user.uid
          ) {
            await deleteUser(userCredential.user).catch(() => {});
            setLoading(false);
            setAlertText({
              heading: "An account already exists with this email.",
              subHeading:
                'Please use the "Continue with Google" button instead.',
            });
            return;
          }
          loginWithCredential(
            userCredential,
            true,
            trimmedFullName,
            signUpPhoneNumber
          );
          mixpanel.track("Webapp: Agreed to Terms", {
            distinct_id: userCredential.user.uid,
            type: "Onboarding",
            customer_email: emailLower,
          });
          await optInToWeeklyCommunityUpdates(
            userCredential.user.uid,
            trimmedFullName,
            signUpPhoneNumber,
            mixpanel,
            "Sign Up"
          );
          setLoading(false);
        }
      })
      .catch((error) => {
        if (error.code === "auth/email-already-in-use") {
          setEmailError({ status: true, message: "Email already exists" });
        } else if (error.code === "auth/invalid-email") {
          setEmailError({ status: true, message: "Poorly formatted email" });
        } else if (error.code === "auth/weak-password") {
          setPasswordError({
            status: true,
            message: "Weak password. Please try another.",
          });
        } else {
          setEmailError({ status: true, message: error.message });
        }
        setLoading(false);
      });
  }, [
    confirmPassword,
    email,
    loginWithCredential,
    mixpanel,
    password,
    signUpFullName,
    signUpPhoneNumber,
  ]);

  const continueWithPhone = useCallback(
    (defaultSubscriber?: boolean) => {
      if (defaultSubscriber) {
        navigate(`${NavigationId.SIGN_IN}?subscriber`, { replace: true });
      }
      setLoginStage(LoginStage.PHONE);
    },
    [navigate]
  );

  const renderBodyTitle = useMemo(
    () => (currentRoleTicket ? "Accept Organizer Role" : "Welcome to Markit"),
    [currentRoleTicket]
  );

  const renderBodySubtext = useCallback(
    (subtext: string) =>
      currentRoleTicket && currentEventForRole ? (
        <span>
          You&apos;ve been invited as a {friendlyRoleName(currentRoleTicket)}{" "}
          for{" "}
          <a
            style={{ color: Colors.BLACK, textDecoration: "underline" }}
            href={`${getShareDomain()}/e/${currentEventForRole.id}`}
            target="_blank"
            rel="noreferrer"
          >
            {currentEventForRole.title}
          </a>
          <br />
          Sign in or sign up below
          {currentRoleTicket ? " to accept this role." : ""}
        </span>
      ) : (
        subtext
      ),
    [currentEventForRole, currentRoleTicket]
  );

  const renderEmailLogin = useMemo(
    () => (
      <LoginWelcomeBody
        title={renderBodyTitle}
        subtext={renderBodySubtext(
          "Sign in with your email and password below"
        )}
      >
        <div className="ColumnNormal" style={{ gap: 14, width: "100%" }}>
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <CustomTextField
              value={email}
              placeholder="Email"
              inputMode="text"
              borderRadius={12}
              onChange={(change: any) => {
                setEmailError({
                  status: false,
                  message: "",
                });
                setEmail(change.target.value);
              }}
              error={emailError.status}
            />
            {emailError.status && emailError.message ? (
              <span style={styles.errorMessage}>{emailError.message}</span>
            ) : null}
          </div>
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <CustomTextField
              inputMode={showPassword ? "text" : "password"}
              value={password}
              placeholder="Password"
              error={passwordError.status}
              onChange={(password: any) => {
                setPasswordError({ status: false, message: "" });
                setPassword(password.target.value);
              }}
              endAdornment={
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleClickShowPassword}
                >
                  {showPassword ? <MdVisibility /> : <MdVisibilityOff />}
                </IconButton>
              }
            />
            {passwordError.status && passwordError.message ? (
              <span style={styles.errorMessage}>{passwordError.message}</span>
            ) : null}
          </div>
        </div>
        <RectangleButton
          buttonLabel={<span>Continue</span>}
          onPress={logInWithEmailAndPassword}
          theme={theme}
          altPaddingVert={12}
          disabled={loading}
          loading={loading}
        />
        <SocialSignInButtons isSignUp={false} />
        <div className="ColumnCentering" style={{ gap: 10 }}>
          {currentRoleTicket ? (
            <TextHoverButton
              text="New to Markit? Sign Up"
              onPress={roleSignUpOnPress}
              icon={
                <Icon
                  icon="ion:chevron-forward"
                  height={12}
                  style={{ color: Colors.BLACK, marginBottom: -1 }}
                />
              }
              iconPosition="right"
              textStyles={{ color: Colors.BLACK, fontWeight: 500 }}
            />
          ) : null}
          <div className="AlignedRow" style={{ gap: 20 }}>
            <TextHoverButton
              text="Forgot Password?"
              onPress={navigateForgotPassword}
              textStyles={{
                color: currentRoleTicket ? Colors.GRAY1 : Colors.BLACK,
              }}
            />
            {!currentRoleTicket ? (
              <TextHoverButton
                text="Continue with Phone"
                icon={
                  <Icon
                    icon={"ion:phone-portrait-outline"}
                    height={13}
                    style={{ color: Colors.GRAY1 }}
                  />
                }
                onPress={() => continueWithPhone(false)}
              />
            ) : null}
          </div>
        </div>
        {!currentRoleTicket ? (
          <span style={{ fontSize: 12, color: Colors.GRAY2 }}>
            If you are a subscriber, you must continue with phone
          </span>
        ) : null}
      </LoginWelcomeBody>
    ),
    [
      continueWithPhone,
      currentRoleTicket,
      email,
      emailError.message,
      emailError.status,
      handleClickShowPassword,
      loading,
      logInWithEmailAndPassword,
      navigateForgotPassword,
      password,
      passwordError.message,
      passwordError.status,
      renderBodySubtext,
      renderBodyTitle,
      roleSignUpOnPress,
      showPassword,
      styles.errorMessage,
      theme,
    ]
  );

  const renderInputIcon = useCallback(
    (name: string, isDefined: boolean, isError?: boolean) => (
      <Icon
        icon={name}
        height={22}
        style={{
          color: isError
            ? Colors.RED3
            : isDefined
              ? Colors.BLACK
              : Colors.GRAY2,
          marginLeft: 14,
        }}
      />
    ),
    []
  );

  const renderEmailSignUp = useMemo(
    () => (
      <>
        {currentRoleTicket ? (
          <div
            style={
              isDesktop
                ? { position: "absolute", left: 20, top: 20 }
                : { display: "flex", paddingLeft: 14 }
            }
          >
            <ContainerButton
              onPress={() => setLoginStage(LoginStage.EMAIL_LOGIN)}
              theme={LightTheme}
            />
          </div>
        ) : null}
        <LoginWelcomeBody
          title={renderBodyTitle}
          subtext={renderBodySubtext(
            "Sign up with your email to continue as a creator."
          )}
        >
          <div className="ColumnNormal" style={{ gap: 14, width: "100%" }}>
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <span className="bodySmallMedium">Account Name</span>
              <CustomTextField
                value={signUpFullName}
                placeholder="What do you want to go by?"
                inputMode="text"
                borderRadius={12}
                onChange={(change: any) => {
                  setSignUpFullNameError({ status: false, message: "" });
                  setSignUpFullName(change.target.value);
                }}
                error={signUpFullNameError.status}
                altPadding={"8px 7px"}
                startAdornment={renderInputIcon(
                  "ion:person",
                  !!signUpFullName,
                  signUpFullNameError.status
                )}
              />
              {signUpFullNameError.status ? (
                <span style={styles.errorMessage}>
                  {signUpFullNameError.message}
                </span>
              ) : null}
            </div>
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <span className="bodySmallMedium">Email</span>
              <CustomTextField
                value={email}
                placeholder="Email"
                inputMode="text"
                borderRadius={12}
                onChange={(change: any) => {
                  setEmailError({
                    status: false,
                    message: "",
                  });
                  setEmail(change.target.value);
                }}
                error={emailError.status}
                altPadding={"8px 7px"}
                startAdornment={renderInputIcon(
                  "ic:outline-email",
                  !!email,
                  emailError.status
                )}
              />
              {emailError.status ? (
                <span style={styles.errorMessage}>{emailError.message}</span>
              ) : null}
            </div>
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <span className="bodySmallMedium">Phone Number</span>
              <CustomPhoneTextField
                placeholder="Enter phone number"
                phoneNumber={signUpPhoneNumber}
                onPhoneChange={(value) => {
                  setSignUpPhoneNumberError({ isError: false, message: "" });
                  setSignUpPhoneNumber((value as string) || "");
                }}
                showPhoneError={signUpPhoneNumberError}
                startAdornment={
                  <Icon
                    icon="mdi:phone"
                    height={22}
                    style={{
                      color: signUpPhoneNumberError.isError
                        ? Colors.RED3
                        : signUpPhoneNumber
                          ? Colors.BLACK
                          : Colors.GRAY2,
                      marginBottom: -3,
                    }}
                  />
                }
                autoCompleteOn
              />
            </div>
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <span className="bodySmallMedium">Password</span>
              <CustomTextField
                inputMode={showPassword ? "text" : "password"}
                value={password}
                placeholder="Password"
                error={passwordError.status}
                altPadding={"8px 7px"}
                onChange={(password: any) => {
                  setPasswordError({ status: false, message: "" });
                  setPassword(password.target.value);
                }}
                autocompletePassword
                startAdornment={renderInputIcon(
                  "mdi:password",
                  !!password,
                  passwordError.status
                )}
                endAdornment={
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleClickShowPassword}
                  >
                    {showPassword ? <MdVisibility /> : <MdVisibilityOff />}
                  </IconButton>
                }
              />
              {passwordError.status ? (
                <span style={styles.errorMessage}>{passwordError.message}</span>
              ) : null}
            </div>
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <span className="bodySmallMedium">Confirm Password</span>
              <CustomTextField
                inputMode={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                placeholder="Confirm Password"
                error={passwordError.status}
                altPadding={"8px 7px"}
                onChange={(password: any) => {
                  setPasswordError({ status: false, message: "" });
                  setConfirmPassword(password.target.value);
                }}
                autocompletePassword
                startAdornment={renderInputIcon(
                  "mdi:password",
                  !!confirmPassword,
                  passwordError.status
                )}
                endAdornment={
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowConfirmPassword}
                    onMouseDown={handleClickShowConfirmPassword}
                  >
                    {showConfirmPassword ? (
                      <MdVisibility />
                    ) : (
                      <MdVisibilityOff />
                    )}
                  </IconButton>
                }
              />
            </div>
          </div>
          <RectangleButton
            buttonLabel={<span>Create Account</span>}
            onPress={registerEmailAndPassword}
            theme={theme}
            altPaddingVert={12}
            disabled={loading}
            loading={loading}
          />
          <SocialSignInButtons isSignUp={true} />
          <WeeklyUpdatesDisclosure />
        </LoginWelcomeBody>
      </>
    ),
    [
      confirmPassword,
      currentRoleTicket,
      email,
      emailError.message,
      emailError.status,
      handleClickShowConfirmPassword,
      handleClickShowPassword,
      loading,
      password,
      passwordError.message,
      passwordError.status,
      registerEmailAndPassword,
      renderBodySubtext,
      renderBodyTitle,
      renderInputIcon,
      showConfirmPassword,
      showPassword,
      signUpFullName,
      signUpFullNameError.message,
      signUpFullNameError.status,
      signUpPhoneNumber,
      signUpPhoneNumberError,
      styles.errorMessage,
      theme,
    ]
  );

  const renderAccountType = useMemo(
    () => (
      <LoginOnboardingAccountType
        creatorOnPress={() => setLoginStage(LoginStage.EMAIL_SIGNUP)}
        subscriberOnPress={() => continueWithPhone(true)}
      />
    ),
    [continueWithPhone]
  );

  const renderPhoneLogin = useMemo(
    () => (
      <LoginWelcomeBody
        title={renderBodyTitle}
        subtext={renderBodySubtext(
          defaultSubscriber
            ? "If you are a subscriber, you must continue with phone to receive texts"
            : "Sign in or sign up with your phone number below"
        )}
      >
        <div className="ColumnNormal" style={{ gap: 20, width: "100%" }}>
          <CustomPhoneTextField
            placeholder="Enter Phone Number"
            phoneNumber={tempPhoneNumber}
            onPhoneChange={(value) => {
              setShowPhoneNumberError({ isError: false, message: "" });
              setTempPhoneNumber(value as string);
            }}
            showPhoneError={showPhoneNumberError}
            disabled={
              optedOut || verificationState !== VerificationState.UNVERIFIED
            }
            autoCompleteOn
          />
          <VerificationCodeButton
            origin={VerificationCodeOrigin.SIGN_IN}
            buttonText="Continue"
            tempPhoneNumber={tempPhoneNumber}
            verificationState={verificationState}
            setVerificationState={setVerificationState}
            optedOut={optedOut}
            setOptedOut={setOptedOut}
            showCodeInputError={showCodeInputError}
            setShowCodeInputError={setShowCodeInputError}
            showError={() =>
              showPhoneInputError(tempPhoneNumber, setShowPhoneNumberError)
            }
          />
        </div>
        {!defaultSubscriber &&
        (verificationState === VerificationState.UNVERIFIED || optedOut) ? (
          <>
            <TextHoverButton
              text="Are you a creator? Continue with email"
              onPress={navigateEmailLoginOnPress}
              icon={
                <Icon
                  icon="ion:mail"
                  height={13}
                  style={{ color: Colors.BLACK }}
                />
              }
              textStyles={{ color: Colors.BLACK }}
            />
            <span
              style={{
                fontSize: 12,
                color: Colors.GRAY2,
                textAlign: "center",
                paddingInline: isDesktop ? 0 : 30,
              }}
            >
              Email is required for creator accounts on Markit
            </span>
          </>
        ) : null}
      </LoginWelcomeBody>
    ),
    [
      defaultSubscriber,
      navigateEmailLoginOnPress,
      optedOut,
      renderBodySubtext,
      renderBodyTitle,
      showCodeInputError,
      showPhoneNumberError,
      tempPhoneNumber,
      verificationState,
    ]
  );

  return (
    <RecaptchaVerifierWrapper>
      <Helmet>
        <title>Sign In Sign Up | Markit AI</title>
        <meta
          name="description"
          content="Sign in to your Markit AI account, or sign up and start texting for free. Start texting in minutes, and monetize by hosting products, memberships, and more with Markit AI."
        />
        <link rel="canonical" href={MARKITAI + NavigationId.SIGN_IN} />
      </Helmet>
      <div
        className="LoginContainer"
        style={{ height: "100vh", position: "relative" }}
      >
        <div
          className="ColumnNormal"
          style={{
            margin: isDesktop ? "auto" : undefined,
            gap: 30,
            width: isDesktop ? 544 : undefined,
            marginTop: 60,
          }}
        >
          {loginStage === LoginStage.EMAIL_LOGIN ? (
            renderEmailLogin
          ) : loginStage === LoginStage.EMAIL_SIGNUP ? (
            renderEmailSignUp
          ) : loginStage === LoginStage.ACCOUNT_TYPE ? (
            renderAccountType
          ) : loginStage === LoginStage.FORGOT_PASSWORD ? (
            <LoginForgotPassword
              email={email}
              setEmail={setEmail}
              emailError={emailError}
              setEmailError={setEmailError}
              backToEmailOnPress={backToEmailSignIn}
            />
          ) : (
            renderPhoneLogin
          )}
        </div>
      </div>
      <AlertContainer
        headerComp={alertText.heading}
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        theme={theme}
        closeModal={() => setAlertText({ heading: "", subHeading: "" })}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
      />
    </RecaptchaVerifierWrapper>
  );
});

export default LoginWelcome;
